import clsx from 'clsx';
import {
  type FocusEvent,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';

import { useControllableState } from '../../core/hooks/useControllableState.js';
import { DataTestId } from '../../core/types/data-props.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { useInternalFormRegistration } from '../form/hooks/useInternalFormRegistration.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { withFormFieldMessageContextProvider } from '../forms-core/components/form-field/FormFieldMessageContextProvider.js';
import { useFormControl } from '../forms-core/hooks/useFormControl.js';
import { useFormFieldContext } from '../forms-core/hooks/useFormFieldContext.js';
import { getI18nValidationState } from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useContainerFocus } from '../hooks/useContainerFocus.js';
import type { FormControlRef } from '../shared-types.js';
import type { MessageTarget } from '../validation/message-routing.js';
import type { ValidationState } from '../validation/types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';
import { RadioGroupContext } from './RadioGroupContext.js';
import * as styles from './styles.sty.js';

// #region Typings
/**
 * Properties for the RadioGroup component
 * @public
 */
export type RadioGroupProps = FormControlProps<
  string,
  (value: string, event?: FocusEvent<HTMLInputElement>) => void | never,
  HTMLDivElement
> &
  StylingProps &
  WithChildren &
  DataTestId &
  MaskingProps & {
    /**
     * Internal: associates this control with a `_Form` rendered outside its
     * React subtree via the native `form` attribute. Unstable.
     * @internal
     */
    _form?: string;
    /**
     * Internal: routes this control's native/Strato validation messages to the
     * field outlet (default), the enclosing form's outlet, or both. Unstable.
     * @internal
     */
    _messageTarget?: MessageTarget | MessageTarget[];
  };
// #endregion

const RadioGroupRoot = /* @__PURE__ */ forwardRef<
  FormControlRef,
  RadioGroupProps
>((props, forwardedRef) => {
  const {
    children,
    id: propId,
    disabled: propDisabled,
    value,
    name,
    _form,
    _messageTarget,
    defaultValue,
    // oxlint-disable-next-line typescript/no-deprecated -- APPDEV-18321
    controlState,
    onChange,
    onValidityChange,
    onFocus,
    onBlur,
    readOnly: propReadOnly,
    required: propRequired,
    'aria-disabled': propAriaDisabled,
    'aria-describedby': ariaDescribedby,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  }: RadioGroupProps = props;

  const {
    id,
    disabled,
    required,
    readOnly: _readOnly,
    ariaDisabled,
    inputProps,
    hintProps,
    hint,
    showError,
    messageDispatch: messagesContextDispatch,
  } = useFormControl({
    propId,
    propDisabled,
    propRequired,
    propAriaDisabled,
    propReadOnly,
    controlState,
  });
  const readOnly = !!_readOnly;
  const { formFieldLabelId } = useFormFieldContext();
  const groupLabelledby =
    ariaLabelledby ?? (ariaLabel === undefined ? formFieldLabelId : undefined);

  const [controllableState, setControllableState] = useControllableState({
    defaultValue,
    value,
    onChange,
  });

  const intl = useIntl();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const formValidityChangeRef = useRef<
    ((state: ValidationState) => void) | undefined
  >(undefined);
  const _emitValidationState = useValidationStateEmitter(
    controlState
      ? undefined
      : (state) => formValidityChangeRef.current?.(state),
  );
  const emitValidationState = useCustomMessageEmitter(
    _emitValidationState,
    () =>
      getI18nValidationState(
        intl,
        wrapperRef.current?.querySelector('input'),
        'manual',
        id,
      ),
  );

  const formControl = useInternalFormRegistration({
    id,
    name,
    form: _form,
    messageTarget: _messageTarget,
    onValidityChange,
    getValue: () => controllableState ?? null,
    focus: () => {
      const checkedInput = wrapperRef.current?.querySelector(
        'input:checked',
      ) as HTMLInputElement | null;
      (
        checkedInput ??
        (wrapperRef.current?.querySelector('input') as HTMLInputElement | null)
      )?.focus({ focusVisible: true });
    },
    validateField: ({ reveal, trigger }) => {
      wrapperRef.current
        ?.querySelectorAll('input')
        ?.forEach((input) => input.checkValidity());
      if (reveal) {
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      }
      const firstInput = wrapperRef.current?.querySelector(
        'input',
      ) as HTMLInputElement | null;
      return Promise.resolve(
        emitValidationState(
          getI18nValidationState(intl, firstInput, trigger, id),
        ),
      );
    },
    resetValidation: () => {
      wrapperRef.current
        ?.querySelectorAll('input')
        ?.forEach((input) => input.setCustomValidity(''));
      messagesContextDispatch?.({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: false, resetShowError: true },
      });
    },
  });
  formValidityChangeRef.current = formControl.onValidityChange;

  useImperativeHandle(forwardedRef, (): FormControlRef<HTMLDivElement> => {
    return {
      element: wrapperRef.current,
      focus() {
        const checkedInput = wrapperRef.current?.querySelector(
          'input:checked',
        ) as HTMLInputElement | null;
        (
          checkedInput ??
          (wrapperRef.current?.querySelector(
            'input',
          ) as HTMLInputElement | null)
        )?.focus();
      },
      validate() {
        if (controlState !== undefined) {
          return;
        }
        wrapperRef.current
          ?.querySelectorAll('input')
          ?.forEach((i) => i.checkValidity());
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        const firstInput = wrapperRef.current?.querySelector(
          'input',
        ) as HTMLInputElement | null;
        emitValidationState(
          getI18nValidationState(intl, firstInput, 'manual', id),
        );
      },
      // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
      validateAsync: async () => {
        const firstInput = wrapperRef.current?.querySelector(
          'input',
        ) as HTMLInputElement | null;
        if (controlState !== undefined) {
          return emitValidationState(
            getI18nValidationState(intl, firstInput, 'manual', id),
          );
        }
        wrapperRef.current
          ?.querySelectorAll('input')
          ?.forEach((i) => i.checkValidity());
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        return emitValidationState(
          getI18nValidationState(intl, firstInput, 'manual', id),
        );
      },
    };
  });

  if (isDev() && name === undefined) {
    // oxlint-disable-next-line no-console
    console.warn(
      'RadioGroup: The "name" prop is missing, which is necessary to link the radio buttons correctly.',
    );
  }

  const radioGroupContextValue = useMemo(
    () => ({
      name,
      form: _form,
      isDisabled: disabled,
      ariaDisabled,
      hasError: showError,
      value: controllableState,
      onChange: setControllableState,
      readOnly,
      required,
      controlState,
      onValidityChange: emitValidationState,
    }),
    [
      name,
      _form,
      disabled,
      ariaDisabled,
      showError,
      controllableState,
      setControllableState,
      readOnly,
      required,
      controlState,
      emitValidationState,
    ],
  );

  const { containerClassName, ...containerProps } =
    useContainerFocus<HTMLDivElement>({
      onFocus,
      onBlur,
      disabled,
      emitValidationState,
    });

  return (
    <div
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-orientation="vertical"
      role="radiogroup"
      aria-live="polite"
      aria-label={ariaLabel}
      aria-disabled={propAriaDisabled}
      aria-labelledby={groupLabelledby}
      ref={wrapperRef}
      className={clsx(containerClassName, consumerClassName, styles.group, {
        [styles.readOnly]: readOnly,
        [styles.hasError]: showError,
      })}
      style={consumerStyle}
      id={id}
      aria-describedby={
        [ariaDescribedby, inputProps['aria-describedby']]
          .filter(Boolean)
          .join(' ') || undefined
      }
      aria-invalid={inputProps['aria-invalid']}
      {...containerProps}
      {...remainingProps}
    >
      <RadioGroupContext.Provider value={radioGroupContextValue}>
        <Flex flexDirection="column" gap={8}>
          {children}
        </Flex>
      </RadioGroupContext.Provider>
      {hint && (
        <BaseItem
          variant={showError ? 'error' : 'hint'}
          disabled={disabled || ariaDisabled}
          {...hintProps}
        >
          {hint}
        </BaseItem>
      )}
    </div>
  );
});

/**
 * Radio buttons allow users to select one option from
 * a group of related options. To group options, wrap the
 * `Radio` components inside a 'RadioGroup' component.
 * @public
 */
export const RadioGroup = withFormFieldMessageContextProvider<
  RadioGroupProps,
  FormControlRef,
  HTMLDivElement
>(RadioGroupRoot);

(RadioGroup as typeof RadioGroup & { displayName: string }).displayName =
  'RadioGroup';
